import { cognitoConfig } from "./config";

const STORAGE_PREFIX = "CognitoIdentityServiceProvider";

/**
 * How long (in seconds) a stored ID token must remain valid for us to treat it
 * as "fresh". Chosen to be far above Amplify's own expiry tolerance so that
 * whenever we report a token as fresh, Amplify's `fetchAuthSession` is
 * guaranteed to serve it from cache without issuing a network refresh — which
 * is what makes it safe to skip the cross-tab refresh lock for these reads.
 */
const FRESHNESS_MARGIN_SECONDS = 120;

/**
 * Decodes a JWT payload without verification (client-side expiry inspection
 * only). Self-contained twin of the private helper in
 * `capacitorCognitoSession.ts` — kept local so this module has no dependency
 * on the Capacitor reader (which tests mock wholesale).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  try {
    const [, payloadSegment = ""] = token.split(".");
    const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Returns true when localStorage holds an Amplify-format Cognito ID token for
 * the last authenticated user that stays valid for at least
 * `FRESHNESS_MARGIN_SECONDS`.
 *
 * Used by `fetchAuthSessionWithLock` to decide whether a token read can skip
 * the cross-tab Web Lock: the lock only exists to serialize refresh-token
 * rotation, and a comfortably-unexpired ID token means Amplify will serve the
 * cached session without refreshing, so there is no rotation to guard.
 *
 * Deliberately conservative: any anomaly (no user, missing token, undecodable
 * payload, missing/odd `exp`) returns false, routing the caller onto the
 * locked path.
 */
export function hasFreshStoredIdToken(): boolean {
  try {
    const prefix = `${STORAGE_PREFIX}.${cognitoConfig.clientId}`;
    const username = localStorage.getItem(`${prefix}.LastAuthUser`);
    if (!username) {
      return false;
    }

    const idToken = localStorage.getItem(`${prefix}.${username}.idToken`);
    if (!idToken) {
      return false;
    }

    const payload = decodeJwtPayload(idToken);
    const exp = payload?.exp;
    if (typeof exp !== "number") {
      return false;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp - FRESHNESS_MARGIN_SECONDS > nowSeconds;
  } catch {
    // localStorage access can throw (e.g. privacy modes); fail closed onto the
    // locked path.
    return false;
  }
}
